import React, { useState } from 'react';
import { AlertCircle, FileText, X, UploadCloud, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/Button';
interface UpdateDocumentJourneyProps {
  onComplete: () => void;
  onCancel: () => void;
  adminNote: string;
}
export function UpdateDocumentJourney({
  onComplete,
  onCancel,
  adminNote
}: UpdateDocumentJourneyProps) {
  const [isReplaced, setIsReplaced] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [newFile, setNewFile] = useState<File | null>(null);
  const handleRemoveAndReplace = () => {
    setIsReplaced(true);
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setIsUploading(true);
      // Simulate upload delay
      setTimeout(() => {
        setNewFile(e.target.files![0]);
        setIsUploading(false);
      }, 1500);
    }
  };
  const handleSimulateUpload = () => {
    setIsUploading(true);
    setTimeout(() => {
      // Create a mock file for the simulation
      const mockFile = new File([new Uint8Array(Math.round(1024 * 1024 * 0.74))], 'ssm-section-17-clear-scan.pdf', {
        type: 'application/pdf'
      });
      setNewFile(mockFile);
      setIsUploading(false);
    }, 1500);
  };
  return <div className="flex flex-col h-full animate-in fade-in duration-500 max-w-2xl mx-auto w-full" data-id="element-588">
      <div className="flex-1 overflow-y-auto px-4 py-8 sm:px-6 lg:px-8 custom-scrollbar" data-id="element-589">
        {/* Header */}
        <div className="mb-8" data-id="element-590">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-591">
            Update SSM Document
          </h1>
          <p className="text-gray-600" data-id="element-592">
            Please provide a clearer copy of your business registration
            document.
          </p>
        </div>

        {/* Alert Banner */}
        <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3" data-id="element-593">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" data-id="element-594" />
          <p className="text-sm text-red-900 font-medium" data-id="element-595">
            Reason for return: {adminNote}
          </p>
        </div>

        <div className="space-y-8" data-id="element-596">
          {/* Rejected File UI */}
          {!isReplaced && <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-id="element-597">
              <h3 className="text-sm font-semibold text-gray-900 mb-4" data-id="element-598">
                Previously Uploaded File
              </h3>
              <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg" data-id="element-599">
                <div className="flex items-center gap-4" data-id="element-600">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-601">
                    <FileText className="w-5 h-5 text-red-600" data-id="element-602" />
                  </div>
                  <div data-id="element-603">
                    <p className="text-sm font-medium text-gray-900" data-id="element-604">
                      ssm-section-17.pdf
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5" data-id="element-605">0.68 MB</p>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2" data-id="element-606">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700 border border-red-200" data-id="element-607">
                    Rejected
                  </span>
                  <button onClick={handleRemoveAndReplace} className="text-sm font-semibold text-red-600 hover:text-red-800 underline underline-offset-2" data-id="element-608">
                    Remove & Replace
                  </button>
                </div>
              </div>
            </div>}

          {/* New Upload Zone */}
          {(isReplaced || newFile) && <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-609">
              <h3 className="text-sm font-semibold text-gray-900 mb-4" data-id="element-610">
                Upload New Document
              </h3>

              {!newFile ? <div className="space-y-3" data-id="element-611">
                  <div className="relative border-2 border-dashed border-[#FFCC00] rounded-xl p-8 bg-yellow-50/30 hover:bg-yellow-50/50 transition-colors text-center" data-id="element-612">
                    <input type="file" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileChange} disabled={isUploading} data-id="element-613" />

                    {isUploading ? <div className="flex flex-col items-center justify-center py-4" data-id="element-614">
                        <div className="w-10 h-10 border-4 border-gray-200 border-t-[#FFCC00] rounded-full animate-spin mb-4" data-id="element-615"></div>
                        <p className="text-sm font-medium text-gray-900" data-id="element-616">
                          Uploading document...
                        </p>
                      </div> : <div className="flex flex-col items-center justify-center" data-id="element-617">
                        <div className="w-12 h-12 bg-[#FFCC00]/20 rounded-full flex items-center justify-center mb-4" data-id="element-618">
                          <UploadCloud className="w-6 h-6 text-gray-900" data-id="element-619" />
                        </div>
                        <p className="text-base font-semibold text-gray-900 mb-1" data-id="element-620">
                          Click or drag file to upload
                        </p>
                        <p className="text-sm text-gray-500" data-id="element-621">
                          Please upload a clear, high-resolution PDF or JPG. Max
                          size 10MB.
                        </p>
                      </div>}
                  </div>

                  {!isUploading && <div className="flex justify-center" data-id="element-622">
                      <button type="button" onClick={handleSimulateUpload} className="text-xs font-semibold text-blue-600 hover:text-blue-800 underline underline-offset-2" data-id="element-623">
                        Demo: Simulate file upload
                      </button>
                    </div>}
                </div> : <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg" data-id="element-624">
                  <div className="flex items-center gap-4" data-id="element-625">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-626">
                      <FileText className="w-5 h-5 text-green-700" data-id="element-627" />
                    </div>
                    <div data-id="element-628">
                      <p className="text-sm font-medium text-gray-900" data-id="element-629">
                        {newFile.name}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-630">
                        {(newFile.size / (1024 * 1024)).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-631">
                    <CheckCircle2 className="w-5 h-5 text-green-600" data-id="element-632" />
                    <button onClick={() => setNewFile(null)} className="text-gray-400 hover:text-gray-600 p-1" aria-label="Remove file" data-id="element-633">
                      <X className="w-4 h-4" data-id="element-634" />
                    </button>
                  </div>
                </div>}
            </div>}
        </div>
      </div>

      {/* Sticky Footer */}
      <div className="flex-shrink-0 border-t border-gray-200 bg-white p-4 sm:px-6 lg:px-8" data-id="element-635">
        <div className="flex items-center justify-between gap-3 max-w-2xl mx-auto" data-id="element-636">
          <Button variant="outline" onClick={onCancel} data-id="element-637">
            Cancel
          </Button>
          <Button onClick={onComplete} disabled={!newFile} data-id="element-638">
            Save & Return to Dashboard
          </Button>
        </div>
      </div>
    </div>;
}